import { BarChart, BarOrientation } from '@kubit-ui-web/react-charts';
import {
  BREAK_CARD_ID,
  UNKNOWN_CARD_ID,
  estimateCards,
  type Deck,
  type VoteSummary,
} from '@point-ker/shared';
import { useState } from 'react';
import styled from 'styled-components';

import { LogoMark } from '@/ui/icons';
import { Hint, Row, Spread, Stack } from '@/ui/layout';

/**
 * The round's vote distribution, as vertical bars.
 *
 * Every estimate card gets a place on the axis, including the ones nobody
 * picked. The gaps are the point: a room split between 3 and 21 looks nothing
 * like a room clustered on 5 and 8, and dropping the empty cards would flatten
 * both into the same two bars.
 *
 * `?` and `☕` stay off the axis and are counted underneath instead. They name
 * no quantity, so a bar for them would sit on a scale they are not part of and
 * read as an estimate between two real cards.
 *
 * Counts, not percentages: with a handful of voters a percentage reads as more
 * precision than the sample can support.
 */

/**
 * One hue for every bar. The bars encode magnitude, and magnitude is a single
 * measure — colouring them individually would imply a category that isn't
 * there. Deliberately not the red/green/blue of `VOTE_COLOR` in the results
 * panel: those carry meaning per voter, and reusing them here would suggest
 * this chart means the same thing.
 */
const BAR_COLOR = 'rgb(223, 43, 81)';
const AXIS_COLOR = 'rgba(0, 0, 0, 0.22)';
const LABEL_COLOR = 'rgba(0, 0, 0, 0.55)';

/**
 * The box the chart is drawn into, and the units every size below is in.
 *
 * `BarChart` documents `width`/`height` as the way to size its canvas and
 * deprecates `canvasConfig`, but it defaults `canvasConfig` to a value and its
 * hook takes that branch first (`if (canvasConfig) return canvasConfig`), so
 * the props never reach the measurement. The drawing therefore lives in a
 * fixed `0 0 100 80` viewBox whatever the element measures, and
 * `preserveAspectRatio` scales it to fit — which is why the sizes here are
 * small numbers and land on screen several times larger.
 *
 * `height` only shapes the frame below; the drawing keeps the library's 80.
 * Passing this back as `canvasConfig` is what would make the two agree and
 * one unit resolve to exactly `width / 100` pixels.
 */
const CANVAS = { width: 100, height: 60 };

const LABEL_SIZE = 3.2;
const BAR_WIDTH = 2.4;
const AXIS_WIDTH = 0.25;

/** Reserves the chart's slot in the card, so the layout does not jump. */
const ChartFrame = styled.div`
  width: 100%;
  aspect-ratio: ${CANVAS.width} / ${CANVAS.height};
`;

interface HoveredBar {
  readonly label: string;
  readonly votes: number;
}

export function VoteDistribution({ deck, results }: { deck: Deck; results: VoteSummary }) {
  const [hovered, setHovered] = useState<HoveredBar | null>(null);

  const counts = new Map(results.tally.map((entry) => [entry.card.id, entry.count]));
  const rows = estimateCards(deck).map((card, index) => ({
    key: String(index),
    label: card.label,
    votes: counts.get(card.id) ?? 0,
  }));

  /**
   * Bars are keyed by the card's place in the deck, never by its face.
   *
   * `getPoints` branches per tick: one that does not parse as a number is
   * matched by string, one that does is matched to the nearest tick by
   * arithmetic. A deck mixing the two — `½` beside 1, 2, 3 — poisons that
   * arithmetic with `NaN`, every comparison goes false, and the lookup walks
   * off the end of the tick array and throws. Positions stay numeric and the
   * axis is relabelled on the way out by `valueFormatter`.
   *
   * `card` stays a string on purpose: any *numeric* field other than `pKey` is
   * folded into the y axis's maximum.
   */
  const data = rows.map((row) => ({ card: row.key, votes: row.votes }));
  const labelFor = (key: string) => rows[Number(key)]?.label ?? key;

  // The scale needs at least two ticks to place a bar between, and a round
  // where everyone picked a different card tops out at one vote, so the axis
  // never shrinks below 0–2.
  const axisMax = Math.max(2, ...data.map((row) => row.votes));
  const unknowns = counts.get(UNKNOWN_CARD_ID) ?? 0;
  const breaks = counts.get(BREAK_CARD_ID) ?? 0;

  return (
    <Stack $gap="0.35rem">
      <ChartFrame>
        <BarChart
          data={data}
          pKey="card"
          orientation={BarOrientation.VERTICAL}
          gapBetweenBars={1}
          width="100%"
          height="100%"
          caption={`Votes per card: ${rows
            .filter((row) => row.votes > 0)
            .map((row) => `${row.label}, ${row.votes}`)
            .join('; ')}`}
        >
          {rows.map((row, index) =>
            // A card nobody picked gets no bar at all. Drawing one of zero
            // height still paints its rounded cap, which shows up as a spike
            // hanging off the baseline.
            row.votes === 0 ? null : (
              <BarChart.Path
                key={row.key}
                dataKey="votes"
                dataIdx={index}
                // `order` is what centres a bar over its tick: left unset, the
                // library offsets every bar to make room for sibling series
                // that this chart does not have.
                order={1}
                endRounded={0}
                tabIndex={-1}
                barConfig={{
                  barWidth: BAR_WIDTH,
                  singleConfig: [{ color: BAR_COLOR, coverage: 100 }],
                }}
                onMouseEnter={() => setHovered({ label: row.label, votes: row.votes })}
                onMouseLeave={() => setHovered(null)}
              />
            ),
          )}
          {/* `textAnchor` is not optional here. The y axis fragment defaults it
              to `middle`, the x axis one does not: it hands the label the tick's
              own x and leaves SVG's `start` in force, so every label sits half
              its width right of the bar it names and the last one runs past the
              viewBox, which the svg clips — "21" rendering as "2". Centring also
              matches the space the library reserves at each end, which is half a
              label wide. */}
          <BarChart.XAxis
            position="BOTTOM"
            stroke={AXIS_COLOR}
            strokeWidth={AXIS_WIDTH}
            showTickLines={false}
            valueFormatter={labelFor}
            tickText={{ fontSize: LABEL_SIZE, fill: LABEL_COLOR, top: 1.2, textAnchor: 'middle' }}
          />
          {/* `right` is the gap between a label and the axis it belongs to;
              the library then reserves the label's measured width to the left
              of it, so this is what keeps the numbers off the axis line. */}
          <BarChart.YAxis
            position="LEFT"
            stroke={AXIS_COLOR}
            strokeWidth={AXIS_WIDTH}
            showTickLines
            tickLine={{ stroke: AXIS_COLOR, strokeWidth: 0.2, strokeDasharray: '1 1.5' }}
            tickValues={{ numeric: { min: 0, max: axisMax, step: 1 } }}
            tickText={{ fontSize: LABEL_SIZE, fill: LABEL_COLOR, right: 2 }}
          />
        </BarChart>
      </ChartFrame>

      <Spread $gap="1rem" $wrap>
        {/* The bars carry the shape; this line carries the exact number, so the
            chart needs no label on every bar to be readable. */}
        <Hint>
          {hovered === null
            ? `${results.countedVotes} counted vote${results.countedVotes === 1 ? '' : 's'}`
            : `${hovered.label} · ${hovered.votes} vote${hovered.votes === 1 ? '' : 's'}`}
        </Hint>

        {/* The cards that never reached the axis. Shown only when someone
            picked one — an empty legend is noise. */}
        {(unknowns > 0 || breaks > 0) && (
          <Row $gap="0.75rem" $width="fit-content">
            {unknowns > 0 && <Hint>{`? ${unknowns}`}</Hint>}
            {breaks > 0 && (
              <Row $gap="0.25rem" $width="fit-content">
                <LogoMark size={16} />
                <Hint>{breaks}</Hint>
              </Row>
            )}
          </Row>
        )}
      </Spread>
    </Stack>
  );
}
